// Offline support — GET response cache + write queue that replays when the
// connection comes back. Used by the axios interceptors in lib/api.js.
//
// Design notes:
// - The service worker deliberately never touches /api requests (iOS PWA
//   auth quirk), so ALL data-layer offline behaviour lives here.
// - GET responses for field-critical endpoints are cached in localStorage
//   so the app can render last-known data with no signal.
// - Whitelisted writes (timesheets, task ticks, safety forms, leave,
//   custom forms, pre-starts, QA) are queued on network failure and
//   replayed in order once back online.
// - Photos/signatures captured offline live in IndexedDB (lib/offlineFiles)
//   and queued bodies reference them as "offline://<id>" until synced.
// - Writes the server rejects are KEPT and marked failed (never silently
//   dropped) so the worker can review/retry/delete them on /app/sync.

import { nzTodayIso } from "@/lib/dates";
import { uploadOfflineFiles } from "@/lib/offlineFiles";

const CACHE_PREFIX = "fl_off:";
const QUEUE_KEY = "fl_offline_queue";

const GET_WHITELIST = [
  "/auth/me",
  "/runsheet/today",
  "/stats",
  "/jobs",
  "/timesheets",
  "/timesheet-records",
  "/leave",
  "/safety",
  "/sssps",
  "/announcements",
  "/gear",
  "/truck-sheets",
  "/form_templates",
  "/form_submissions",
  "/qa",
  "/pre_starts",
];

const WRITE_RULES = [
  { method: "post", re: /^\/timesheets$/, label: "Timesheet" },
  { method: "post", re: /^\/timesheets\/[^/]+\/submit$/, label: "Timesheet submission" },
  { method: "post", re: /^\/job-tasks\/[^/]+\/toggle$/, label: "Task tick" },
  { method: "post", re: /^\/safety$/, label: "Safety report" },
  { method: "post", re: /^\/leave$/, label: "Leave request" },
  { method: "post", re: /^\/form_submissions$/, label: "Form submission" },
  { method: "post", re: /^\/pre_starts$/, label: "Pre-start check" },
  { method: "patch", re: /^\/qa\/records\/[^/]+$/, label: "QA record" },
  { method: "post", re: /^\/qa\/records\/[^/]+\/submit$/, label: "QA submission" },
  { method: "post", re: /^\/truck-sheets\/[^/]+\/loads$/, label: "Truck load" },
  { method: "post", re: /^\/truck-sheets\/[^/]+\/submit$/, label: "Running sheet submission" },
];

function emitChange() {
  try {
    window.dispatchEvent(new CustomEvent("fl-offline-change", {
      detail: { pending: pendingCount(), failed: failedCount() },
    }));
  } catch { /* SSR/test */ }
}

export function purgeLegacyReadCache() {
  Object.keys(localStorage).filter(k => k.startsWith(CACHE_PREFIX) &&
    (!k.includes(':scope:') || /^fl_off:(?:\/api)?\/users(?:[/?{:]|$)/.test(k)))
    .forEach(k => localStorage.removeItem(k));
}

export function sessionEpoch() {
  return localStorage.getItem('fl_api_session_epoch') || '';
}

export function advanceSession() {
  localStorage.setItem('fl_api_session_epoch', `${Date.now()}:${Math.random()}`);
  purgeLegacyReadCache();
  window.dispatchEvent(new Event('fieldlink:session-changed'));
}

export function isCacheableGet(url) {
  if (!url || url.includes("site-audit")) return false;
  const path = url.split("?")[0].replace(/^\/api(?=\/)/, '');
  return GET_WHITELIST.some((p) => path === p || path.startsWith(p + "/"));
}

export function setScheduleCacheIdentity(user) {
  purgeLegacyReadCache();
  const identity = user?.id && user?.org_id ? `${user.org_id}:${user.id}:${user.role}` : '';
  if (localStorage.getItem('fl_schedule_cache_identity') !== identity) {
    advanceSession();
    Object.keys(localStorage).filter(k => k.startsWith(CACHE_PREFIX)).forEach(k => localStorage.removeItem(k));
  }
  if (identity) localStorage.setItem('fl_schedule_cache_identity', identity);
  else {
    localStorage.removeItem('fl_schedule_cache_identity');
    localStorage.removeItem(`${CACHE_PREFIX}/auth/me`);
  }
}

export function cacheKey(config) {
  const params = config.params ? JSON.stringify(config.params) : "";
  const path = (config.url || '').split('?')[0];
  const identity = config.__flScheduleIdentity;
  if (!identity || !isCacheableGet(config.url)) return null;
  if (path.startsWith('/runsheet/') || path === '/stats') {
    return `${CACHE_PREFIX}${config.url}${params}:scope:${identity}:day:${config.params?.day || nzTodayIso()}`;
  }
  return `${CACHE_PREFIX}${config.url}${params}:scope:${identity}`;
}

export function saveCache(key, data) {
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify({ t: Date.now(), data }));
    // Keep visited registers and opened details bounded on field devices.
    const boundedPrefix = key.startsWith(`${CACHE_PREFIX}/timesheets/page`)
      ? `${CACHE_PREFIX}/timesheets/page`
      : key.startsWith(`${CACHE_PREFIX}/timesheet-records/`)
        ? `${CACHE_PREFIX}/timesheet-records/` : null;
    if (boundedPrefix) {
      const limit = boundedPrefix.endsWith("/page") ? 5 : 20;
      Object.keys(localStorage).filter((k) => k.startsWith(boundedPrefix))
        .sort((a, b) => (readCache(b)?.t || 0) - (readCache(a)?.t || 0))
        .slice(limit).forEach((k) => localStorage.removeItem(k));
    }
  } catch {
    // Quota exceeded — drop the offline cache and retry once
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith(CACHE_PREFIX))
        .forEach((k) => localStorage.removeItem(k));
      localStorage.setItem(key, JSON.stringify({ t: Date.now(), data }));
    } catch { /* give up quietly */ }
  }
}

export function readCache(key) {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function isQueueableWrite(method, url) {
  if (!method || !url) return false;
  const m = method.toLowerCase();
  const path = url.split("?")[0];
  return WRITE_RULES.some((r) => r.method === m && r.re.test(path));
}

export function labelForWrite(method, url) {
  const m = (method || "").toLowerCase();
  const path = (url || "").split("?")[0];
  const rule = WRITE_RULES.find((r) => r.method === m && r.re.test(path));
  return rule?.label || "Change";
}

function readQueue() {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
  } catch {
    return [];
  }
}

function writeQueue(items) {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(items)); } catch { /* quota */ }
  emitChange();
}

export function listQueue() {
  return readQueue();
}

export function removeQueued(id) {
  writeQueue(readQueue().filter((i) => i.id !== id));
}

export function pendingCount() {
  return readQueue().filter((i) => !i.failed_at).length;
}

export function failedCount() {
  return readQueue().filter((i) => i.failed_at).length;
}

export function enqueueWrite({ method, url, data }) {
  const items = readQueue();
  items.push({
    id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    method, url, data,
    label: labelForWrite(method, url),
    queued_at: new Date().toISOString(),
  });
  writeQueue(items);
}

// Replays queued writes in order:
// 1. Uploads offline photos/signatures first and rewrites "offline://" refs.
// 2. Sends each write. Network failure stops the run (still offline).
// 3. Server rejections (4xx/5xx) are KEPT in the queue marked failed with
//    the error message — reviewable on /app/sync — instead of being dropped.
export async function replayQueue(api, { includeFailed = false } = {}) {
  let items = readQueue();
  if (!items.length) {
    try { await uploadOfflineFiles(api); } catch { /* still offline */ }
    return { synced: 0, failed: 0 };
  }
  // eslint-disable-next-line no-unused-vars
  let pathMap = {};
  let droppedFiles = [];
  try {
    const res = await uploadOfflineFiles(api);
    pathMap = res.map;
    droppedFiles = res.dropped;
  } catch {
    return { synced: 0, failed: 0 }; // network gone — try later
  }
  let synced = 0;
  let failed = 0;
  for (const item of [...items]) {
    if (item.failed_at && !includeFailed) continue;
    let body = item.data;
    let raw = JSON.stringify(body ?? null);
    if (raw && raw.includes("offline://")) {
      for (const [oid, real] of Object.entries(pathMap)) {
        raw = raw.split(`offline://${oid}`).join(real);
      }
      if (raw.includes("offline://")) {
        // References a file that was rejected by the server — surface it
        // instead of silently blocking forever.
        if (droppedFiles.some((oid) => raw.includes(`offline://${oid}`))) {
          failed += 1;
          items = items.map((i) => (i.id === item.id
            ? { ...i, failed_at: new Date().toISOString(), error: "An attached photo/signature couldn't be uploaded (rejected by the server)." }
            : i));
          writeQueue(items);
        }
        continue; // otherwise its file didn't upload yet — keep for next run
      }
      try { body = JSON.parse(raw); } catch { /* keep original */ }
      items = items.map((i) => (i.id === item.id ? { ...i, data: body } : i));
      writeQueue(items);
    }
    try {
      await api.request({ method: item.method, url: item.url, data: body, __flReplay: true });
      synced += 1;
      items = items.filter((i) => i.id !== item.id);
      writeQueue(items);
    } catch (e) {
      if (!e?.response) break; // offline again — keep the rest queued
      failed += 1;
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : `Rejected (${e.response?.status})`;
      items = items.map((i) => (i.id === item.id
        ? { ...i, failed_at: new Date().toISOString(), error: msg }
        : i));
      writeQueue(items);
    }
  }
  emitChange();
  return { synced, failed };
}
